import { useOidc, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { NotificationEntity } from '@/types/api';

import { useNotifications } from '../api/get-notifications';

const dismissedKey = (userId: string, orgId: string) =>
  `mt:news-notifications:dismissed:${userId}:${orgId}`;
const seenKey = (userId: string, orgId: string) => `mt:news-notifications:seen:${userId}:${orgId}`;
const collapsedKey = (userId: string, orgId: string) =>
  `mt:news-notifications:collapsed:${userId}:${orgId}`;

const readSet = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
};

const writeSet = (key: string, set: Set<string>) => {
  localStorage.setItem(key, JSON.stringify([...set]));
};

export const useNewsNotifications = () => {
  const { userId } = useOidc();
  const { orgId } = useScopeIds();
  const { data: allNotifications = [], isLoading, refetch } = useNotifications();

  const keys = useMemo(
    () =>
      userId && orgId
        ? {
            dismissed: dismissedKey(userId, orgId),
            seen: seenKey(userId, orgId),
            collapsed: collapsedKey(userId, orgId),
          }
        : null,
    [userId, orgId],
  );

  const [dismissedSet, setDismissedSet] = useState<Set<string>>(() =>
    keys ? readSet(keys.dismissed) : new Set(),
  );
  const [seenSet, setSeenSet] = useState<Set<string>>(() =>
    keys ? readSet(keys.seen) : new Set(),
  );
  const [isBannerCollapsed, setIsBannerCollapsed] = useState(() =>
    keys ? localStorage.getItem(keys.collapsed) === 'true' : false,
  );

  // Re-sync from localStorage whenever the user/org changes
  useEffect(() => {
    if (!keys) return;
    setDismissedSet(readSet(keys.dismissed));
    setSeenSet(readSet(keys.seen));
    setIsBannerCollapsed(localStorage.getItem(keys.collapsed) === 'true');
  }, [keys]);

  // Prune stale IDs from dismissed/seen after notifications load
  useEffect(() => {
    if (!keys || isLoading) return;
    const activeIds = new Set(allNotifications.map((n) => n.id));

    setDismissedSet((prev) => {
      const pruned = new Set([...prev].filter((id) => activeIds.has(id)));
      if (pruned.size === prev.size) return prev;
      writeSet(keys.dismissed, pruned);
      return pruned;
    });

    setSeenSet((prev) => {
      const pruned = new Set([...prev].filter((id) => activeIds.has(id)));
      if (pruned.size === prev.size) return prev;
      writeSet(keys.seen, pruned);
      return pruned;
    });
  }, [allNotifications, keys, isLoading]);

  // Banner hides dismissed items; bell keeps all for history (dismissal is banner-only)
  const bannerNotifications = useMemo(
    () =>
      allNotifications.filter(
        (n) => (n.display === 'banner' || n.display === 'both') && !dismissedSet.has(n.id),
      ),
    [allNotifications, dismissedSet],
  );

  const bellNotifications = useMemo(
    () =>
      allNotifications
        .filter((n) => n.display === 'bell' || n.display === 'both')
        .sort((a, b) => b.createdAt - a.createdAt),
    [allNotifications],
  );

  const unseenBellCount = useMemo(
    () => bellNotifications.filter((n) => !seenSet.has(n.id)).length,
    [bellNotifications, seenSet],
  );

  const dismissBanner = useCallback(
    (notification: NotificationEntity) => {
      if (!keys) return;
      const { id } = notification;

      setDismissedSet((prev) => {
        const next = new Set(prev);
        next.add(id);
        writeSet(keys.dismissed, next);
        return next;
      });
      setSeenSet((prev) => {
        const next = new Set(prev);
        next.add(id);
        writeSet(keys.seen, next);
        return next;
      });
    },
    [keys],
  );

  const markAllSeen = useCallback(() => {
    if (!keys) return;
    setSeenSet((prev) => {
      const next = new Set(prev);
      bellNotifications.forEach((n) => next.add(n.id));
      writeSet(keys.seen, next);
      return next;
    });
  }, [keys, bellNotifications]);

  const toggleCollapsed = useCallback(() => {
    if (!keys) return;
    setIsBannerCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(keys.collapsed, String(next));
      return next;
    });
  }, [keys]);

  return {
    allNotifications,
    bannerNotifications,
    bellNotifications,
    isBannerCollapsed,
    unseenBellCount,
    isLoading,
    dismissBanner,
    markAllSeen,
    toggleCollapsed,
    refetch,
  };
};
